"use client";

import { useCallback, useContext, createContext } from "react";
import type { ReactNode } from "react";
import React from "react";

// ============================================================
// Típusok
// ============================================================

export type UixLocale = "hu" | "en";

type TranslationDict = Record<string, unknown>;

interface UixTranslationContextValue {
    locale: UixLocale;
    currency: string;
    dict: TranslationDict;
    setLocale: (locale: UixLocale) => void;
}

// ============================================================
// Kontextus
// ============================================================

const UixTranslationContext = createContext<UixTranslationContextValue>({
    locale: "hu",
    currency: "HUF",
    dict: {},
    setLocale: () => { },
});

// ============================================================
// Provider
// ============================================================

/**
 * Fordítási szótár betöltése.
 * Server-side kompatibilis: a dict-et kívülről kell átadni (SSR-barát).
 */
export function UixTranslationProvider({
    locale,
    currency,
    dict,
    setLocale,
    children,
}: {
    locale: UixLocale;
    currency: string;
    dict: TranslationDict;
    setLocale: (locale: UixLocale) => void;
    children: ReactNode;
}) {
    return (
        <UixTranslationContext.Provider value={{ locale, currency, dict, setLocale }}>
            {children}
        </UixTranslationContext.Provider>
    );
}

// ============================================================
// Hook
// ============================================================

/**
 * useUixTranslation — UIX komponensek fordítási hook-ja.
 *
 * Használat:
 *   const { t } = useUixTranslation();
 *   <button aria-label={t('actions.add_to_cart')}>
 *     {t('actions.add_to_cart')}
 *   </button>
 *
 * Interpoláció:
 *   t('labels.page_of', { current: 2, total: 10 })
 *   → "2 / 10"
 */
export function useUixTranslation() {
    const { locale, currency, dict, setLocale } = useContext(UixTranslationContext);

    const t = useCallback(
        (key: string, params?: Record<string, string | number>): string => {
            // Ponttal elválasztott kulcs feloldása: "actions.add_to_cart"
            const value = key.split(".").reduce<unknown>((acc, segment) => {
                if (acc && typeof acc === "object") {
                    return (acc as Record<string, unknown>)[segment];
                }
                return undefined;
            }, dict);

            // Fallback: ha nincs fordítás, visszaadja a kulcsot
            let result = typeof value === "string" ? value : key;

            // Interpoláció: {current} → 2
            if (params) {
                result = Object.entries(params).reduce(
                    (str, [k, v]) => str.replace(new RegExp(`\\{${k}\\}`, "g"), String(v)),
                    result
                );
            }

            return result;
        },
        [dict]
    );

    return { t, locale, currency, setLocale };
}
